import { ComponentProps, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { NavigationNodeId } from '@pwc/pwc-deals-web-common';
import { ActionBanner } from '@pwc/pwc-deals-ui';
import { useHistory } from 'react-router-dom';
import { History } from 'history';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import { useKFATitleI18n } from 'common/hooks/useKFATitleI18n';
import GroupKFA from '../../models/GroupKFAInterface';
import Group from '../../models/Group';
import {
  addAnalysisTypePath,
  createHypothesisPath,
} from '../../../../common/services/urlService';
import useMobile from '../../../../common/hooks/useMobile';

import './Banner.scss';

type Props = {
  projectId: number;
  noAnalysis: boolean;
  isKFAsPage: boolean;
  isCustomPage: boolean;
  isNonePage?: boolean;
  displayAddGroupRow: () => void;
  analysisCount?: number;
  kfaGroups: GroupKFA[];
  groups: Group[];
  tags: number[];
};
type ArrayItem<T> = T extends (infer U)[] ? U : never;

type DealsUiActionInterface = ArrayItem<
  ComponentProps<typeof ActionBanner>['actions']
>;

const getText = (
  noAnalysis: boolean,
  noGroups: boolean,
  isCustomPage: boolean,
  isKFAsPage: boolean,
  KFATitleI18n: string,
  t: TFunction
): string | null => {
  const NO_ANALYSIS = t('noAnalysesHaveBeenAdded');
  const NO_KFA = t('noKFAHaveBeenAdded', { KFATitle: KFATitleI18n });
  const NO_GROUPS = t('noGroupsHaveBeenAdded');
  if (noAnalysis && noGroups) {
    if (isCustomPage) {
      return NO_ANALYSIS;
    }
    if (isKFAsPage) {
      return NO_KFA;
    }
    return NO_ANALYSIS;
  }
  if (noAnalysis) {
    return NO_ANALYSIS;
  }
  if (noGroups) {
    if (isCustomPage) {
      return NO_GROUPS;
    }
    if (isKFAsPage) {
      return NO_KFA;
    }
    return null;
  }
  return null;
};

const setAsPrimaryAction = (
  action: DealsUiActionInterface
): DealsUiActionInterface => ({ ...action, isPrimary: true });

const useGetActions = (
  noAnalysis: boolean,
  noGroups: boolean,
  isCustomPage: boolean,
  isKFAsPage: boolean,
  history: History,
  projectId: number,
  displayAddGroupRow: () => void,
  KFATitle: string
): DealsUiActionInterface[] => {
  const isMobile = useMobile();
  const { t } = useTranslation();
  const KFATitleI18n = useKFATitleI18n(KFATitle);
  const ADD_ANALYSIS_ACTION = {
    name: t('addAnalysis'),
    action: () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      // eslint-disable-next-line
      history.push(addAnalysisTypePath(projectId));
    },
    disabled: isMobile,
  };
  const ADD_GROUP_ACTION = {
    name: t('addGroup'),
    action: displayAddGroupRow,
  };
  const ADD_KFA_ACTION = {
    name: t('addKFA', { KFATitle: KFATitleI18n }),
    action: () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      // eslint-disable-next-line
      history.push(createHypothesisPath(projectId));
    },
    disabled: isMobile,
  };

  if (noAnalysis && noGroups) {
    if (isCustomPage)
      return [setAsPrimaryAction(ADD_ANALYSIS_ACTION), ADD_GROUP_ACTION];
    if (isKFAsPage)
      return [setAsPrimaryAction(ADD_KFA_ACTION), ADD_ANALYSIS_ACTION];
    return [setAsPrimaryAction(ADD_ANALYSIS_ACTION)];
  }
  if (noAnalysis) return [setAsPrimaryAction(ADD_ANALYSIS_ACTION)];
  if (noGroups) {
    if (isCustomPage) return [setAsPrimaryAction(ADD_GROUP_ACTION)];
    if (isKFAsPage) return [setAsPrimaryAction(ADD_KFA_ACTION)];
  }
  return [];
};

const Banner: React.FC<Props> = ({
  projectId,
  isKFAsPage,
  isCustomPage,
  isNonePage,
  displayAddGroupRow,
  kfaGroups = [],
  groups = [],
  analysisCount,
  tags = [],
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  // eslint-disable-next-line
  const history: History = useHistory();
  const [text, setText] = useState<string | undefined>(undefined);

  const KFATitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);
  const KFATitleI18n = useKFATitleI18n(KFATitle);
  const { t } = useTranslation();

  let noAnalysis = false;
  let noGroups = false;

  if (!tags || tags.length === 0) {
    noAnalysis = analysisCount === 0;
    if (isKFAsPage) {
      noGroups =
        kfaGroups.filter(({ contentId }) => contentId !== -1).length === 0;
    } else if (isCustomPage) {
      noGroups = groups.filter(({ groupId }) => groupId !== -1).length === 0;
    }
  }

  useEffect(() => {
    if (analysisCount !== undefined && analysisCount <= 0) {
      setText(undefined);
    }
    if (tags && tags.length > 0) {
      setText(undefined);
    }
    if (
      (groups === null && kfaGroups === null && !isNonePage) ||
      analysisCount === null ||
      analysisCount === undefined
    ) {
      setText(undefined);
    }
    setText(
      getText(
        noAnalysis,
        noGroups,
        isCustomPage,
        isKFAsPage,
        KFATitleI18n,
        t
      ) as string
    );
  }, [
    noGroups,
    analysisCount,
    groups,
    isCustomPage,
    isKFAsPage,
    isNonePage,
    kfaGroups,
    noAnalysis,
    tags,
    KFATitleI18n,
    t,
  ]);

  const actions = useGetActions(
    noAnalysis,
    noGroups,
    isCustomPage,
    isKFAsPage,
    history,
    projectId,
    displayAddGroupRow,
    KFATitle
  );

  return text ? (
    <ActionBanner text={text} actions={actions} className="action-banner" />
  ) : (
    <></>
  );
};

export default Banner;
